import * as React from 'react'
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group'

import { cn } from '@/lib/utils'

/**
 * Spec 07 · Language switch, and spec 11 · admin filter chips.
 *
 * `segmented` is the hairline-boxed pair from the header — one box,
 * the active half filled with ink. The fill is a pill inset inside the
 * track rather than a square half clipped by `overflow-hidden`: at the
 * header's radius the clip cut the fill's outer corners at a different
 * curve than the border's, and the mismatch read as a dent. `chips` is
 * the admin filter row — separate pills, active one ink-filled.
 */

const ToggleGroup = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Root> & {
    look?: 'segmented' | 'chips'
  }
>(({ className, look = 'segmented', children, ...props }, ref) => (
  <ToggleGroupPrimitive.Root
    ref={ref}
    data-look={look}
    className={cn(
      look === 'segmented'
        ? 'inline-flex items-center gap-1 rounded-full border border-hairline bg-[color:var(--sss-field-bg)] p-1'
        : 'flex flex-wrap items-center gap-2',
      className
    )}
    {...props}
  >
    {children}
  </ToggleGroupPrimitive.Root>
))
ToggleGroup.displayName = ToggleGroupPrimitive.Root.displayName

const ToggleGroupItem = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Item> & {
    look?: 'segmented' | 'chips'
  }
>(({ className, look = 'segmented', children, ...props }, ref) => (
  <ToggleGroupPrimitive.Item
    ref={ref}
    className={cn(
      'cursor-pointer leading-none transition-colors duration-[120ms] ease-standard',
      'focus-visible:outline-none focus-visible:shadow-[0_0_0_2px_var(--sss-bg),0_0_0_5px_var(--sss-ring)]',
      look === 'segmented'
        ? [
            'rounded-full px-3.5 py-2 text-[13px] font-medium text-ink-muted',
            'hover:text-ink',
            'data-[state=on]:bg-ink data-[state=on]:text-ink-inverse',
          ].join(' ')
        : [
            'rounded-full border border-hairline px-3 py-[7px] text-[13px] font-medium text-ink-muted',
            'hover:border-ink hover:text-ink',
            'data-[state=on]:border-ink data-[state=on]:bg-ink data-[state=on]:text-ink-inverse',
          ].join(' '),
      className
    )}
    {...props}
  >
    {children}
  </ToggleGroupPrimitive.Item>
))
ToggleGroupItem.displayName = ToggleGroupPrimitive.Item.displayName

export { ToggleGroup, ToggleGroupItem }
